import * as React from 'react'
import { memo, ReactElement, useMemo } from 'react'
import { Link, useHistory } from 'react-router-dom'
import { bundleIcon, CalendarAddFilled, CalendarAddRegular, EditFilled, EditRegular, PrintFilled, PrintRegular } from '@fluentui/react-icons'
import { Button, Card, makeStyles, mergeClasses, Text, tokens, Tooltip } from '@fluentui/react-components'
import { useTranslation } from 'react-i18next'
import { ViewCRMHeaderProps } from './ViewCRM'
import { PartyType } from '../../../components/Form/Party/PartyForm'
import { LegalPersonDto } from '../../../config/partyConfig'
import PhoneNumberButton from '../../components/Button/PhoneNumberButton'
import EmailButton from '../../components/Button/EmailButton'
import { useApplicationStyles } from '../../../pages/App'
import { isOfficeInitialized, outlookDisplayNewAppointmentForm } from '../../../helpers/outlookHelper'
import { formatRecipients } from '../../components/Dialog/OutlookItem/ComposeOutlookItemDialogBody'
import { isAppContext } from '../../../helpers/appHelper'
import { AppContext } from '../../../config/appConfig'
import useSubmitDocumentGenerationRequest from '../../../hooks/finance/report/useSubmitDocumentGenerationRequest'
import { PartyCardReportDto } from '../../../components/Form/Finance/Report/PartyCardReportForm'
import { DocumentGenerationType } from '../../../hooks/document/useDocumentGenerationHandler'
import { composeEmailClick } from '../../../helpers/partyComposeHelper'

const CalendarAddIcon = bundleIcon(CalendarAddFilled, CalendarAddRegular)
const EditIcon = bundleIcon(EditFilled, EditRegular)
const PrintIcon = bundleIcon(PrintFilled, PrintRegular)

const useStyles = makeStyles({
    headerGrid: {
        display: 'grid',
        gridTemplateColumns: '60% 40%',
        gap: tokens.spacingHorizontalS,
        gridAutoRows: 'max-content',

        '@media screen and (max-width: 700px)': {
            gridTemplateColumns: '1fr',
        },
    },
    headerCard: {
        flexShrink: 0,
    },
    leftContainer: {
        display: 'grid',
        gridTemplateColumns: '25% 1fr',
        gridAutoRows: 'min-content',
        gap: tokens.spacingHorizontalS,
    },
    rightContainer: {
        display: 'flex',
        flexDirection: 'column',
        gap: tokens.spacingHorizontalS,
    },
    contactDetailsContainer: {
        display: 'flex',
        flexDirection: 'row',
        gap: tokens.spacingHorizontalXS,
        marginLeft: '-12px',
    },
    gridSpan2Columns: {
        gridColumn: 'span 2',
    },
    actionsRow: {
        display: 'flex',
        flexDirection: 'row',
        gap: tokens.spacingHorizontalS,
        alignItems: 'center',
        justifyContent: 'flex-start',
    },
})

function ViewCRMHeader(props: ViewCRMHeaderProps): ReactElement {
    const appStyles = useApplicationStyles()
    const history = useHistory()
    const styles = useStyles()
    const { t: translate } = useTranslation()

    const submitRequest = useSubmitDocumentGenerationRequest<PartyCardReportDto>(DocumentGenerationType.PartyCard)

    const editUrl = useMemo(() => {
        return props.crmRelation?.type === PartyType.Legal
            ? `/crm/${props.crmRelationId}/legal/${props.crmRelationId}/edit`
            : `/crm/${props.crmRelationId}/natural/${props.crmRelationId}/edit`
    }, [props.crmRelation?.type, props.crmRelationId])

    const legalPerson = useMemo(
        () => (props.crmRelation.type === PartyType.Legal ? (props.crmRelation as LegalPersonDto) : undefined),
        [props.crmRelation],
    )

    return (
        <Card className={mergeClasses(styles.headerGrid, styles.headerCard)}>
            <div className={styles.leftContainer}>
                <Link to={editUrl} className={styles.gridSpan2Columns}>
                    <Text weight={'semibold'} size={400}>
                        {props.crmRelation?.displayName}
                    </Text>
                </Link>
                {props.crmRelation?.address && (
                    <>
                        <div>{translate('crm.detail.address')}</div>
                        <div className={styles.rightContainer}>
                            <Text>
                                {props.crmRelation.address.street} {props.crmRelation.address.number}
                            </Text>
                            <Text>
                                {props.crmRelation.address.postalCode} {props.crmRelation.address.locality}, {props.crmRelation.address.country}
                            </Text>
                        </div>
                    </>
                )}
                {legalPerson?.poBoxAddress && (
                    <>
                        <div>{translate('crm.detail.poboxaddress')}</div>
                        <div className={styles.rightContainer}>
                            <Text>
                                {legalPerson?.poBoxAddress.street} {legalPerson?.poBoxAddress.number}
                            </Text>
                            <Text>
                                {legalPerson?.poBoxAddress.postalCode} {legalPerson?.poBoxAddress.locality}, {legalPerson?.poBoxAddress.country}
                            </Text>
                        </div>
                    </>
                )}
                {legalPerson?.cocNumber && (
                    <>
                        <div>{translate('crm.detail.coc')}</div>
                        <Text>{legalPerson?.cocNumber}</Text>
                    </>
                )}
                {legalPerson?.cocBranchNumber && (
                    <>
                        <div>{translate('crm.detail.cocBranchNumber')}</div>
                        <Text>{legalPerson?.cocBranchNumber}</Text>
                    </>
                )}
                {legalPerson?.vatNumber && (
                    <>
                        <div>{translate('crm.detail.vat')}</div>
                        <Text>{legalPerson?.vatNumber}</Text>
                    </>
                )}
                {legalPerson?.financeEmail && (
                    <>
                        <div>{translate('crm.detail.financeemail')}</div>
                        <Text>{legalPerson?.financeEmail}</Text>
                    </>
                )}
                {props.crmRelation?.kycFiles?.length > 0 && (
                    <>
                        <div>{translate('crm.detail.kyc')}</div>
                        <div>
                            {props.crmRelation.kycFiles.map(kycFile => (
                                <div key={kycFile.id}>
                                    <Link to={`/file/${kycFile.id}`}>
                                        <Text>
                                            {kycFile.reference} - {kycFile.name}
                                        </Text>
                                    </Link>
                                </div>
                            ))}
                        </div>
                    </>
                )}
                {props.crmRelation?.description && (
                    <>
                        <div>{translate('crm.detail.description')}</div>
                        <div></div>
                        <Text className={styles.gridSpan2Columns}>{props.crmRelation?.description}</Text>
                    </>
                )}
            </div>
            <div className={styles.rightContainer}>
                {props.contacts?.map((contact, index) => (
                    <div key={index} className={styles.rightContainer}>
                        <Link to={`/crm/${props.crmRelation.id}/natural/${contact.id}/edit`}>
                            <Text weight={'semibold'} size={400}>
                                {contact.displayName}
                            </Text>
                        </Link>
                        <div className={styles.contactDetailsContainer}>
                            {contact.phoneNumber && (
                                <div className={appStyles.selfStart}>
                                    <PhoneNumberButton clickValue={contact.phoneNumber} textValue={contact.phoneNumber} />
                                </div>
                            )}
                            {contact.email && (
                                <div className={appStyles.selfStart}>
                                    <EmailButton
                                        textValue={contact.email}
                                        onClick={() => composeEmailClick(contact)()}
                                        buttonProps={{ className: appStyles.selfStart }}
                                    />
                                </div>
                            )}
                            {isOfficeInitialized() && !isAppContext(AppContext.Compose) && (
                                <div>
                                    <Tooltip relationship={'label'} content={translate('action.create.appointment')}>
                                        <Button
                                            size={'small'}
                                            appearance={'transparent'}
                                            icon={<CalendarAddIcon />}
                                            onClick={() => {
                                                outlookDisplayNewAppointmentForm({
                                                    requiredAttendees: formatRecipients([contact]),
                                                    subject: props.crmRelation.displayName,
                                                })
                                            }}
                                        />
                                    </Tooltip>
                                </div>
                            )}
                        </div>
                    </div>
                ))}
            </div>

            <div className={styles.actionsRow}>
                {props.crmRelation?.type === PartyType.Legal && (
                    <Tooltip content={translate('crm.detail.printcard')} relationship={'description'} withArrow>
                        <Button icon={<PrintIcon />} appearance={'subtle'} onClick={() => submitRequest({ partyId: props.crmRelation.id })} />
                    </Tooltip>
                )}
                <Tooltip content={translate('crm.detail.edit')} relationship={'description'} withArrow>
                    <Button icon={<EditIcon />} appearance={'subtle'} onClick={() => history.push(editUrl)} />
                </Tooltip>
            </div>
        </Card>
    )
}

export default memo(ViewCRMHeader)
